<template>
  <v-col md="12">
    <base-material-card
      inline
      icon="mdi-cash-multiple"
      :title="$t('Components.Forms.PriceTable.Titles.ComponentTitle')"
      top-actions
      class="px-5 py-3"
    >
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">
          {{ $t('Components.Forms.PriceTable.Titles.ComponentTitle') }}
        </div>
      </template>

      <template v-slot:top-actions>
        <debounce-v-btn
          class="px-2 ml-1"
          color="success"
          @click="showNewPricePointDialog()"
        >
          {{ $t('Components.Forms.PriceTable.Buttons.NewPricePoint') }}
        </debounce-v-btn>
        <debounce-v-btn
          class="px-2 ml-1 v-btn--tight"
          color="info"
          @click="refreshPrices()"
        >
          {{ $t('Components.Forms.PriceTable.Buttons.RefreshPrices') }}
        </debounce-v-btn>
      </template>

      <v-spacer />

      <v-row
        class="py-4"
      >
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
            v-model="product.name"
            color="primary"
            item-color="primary"
            :label="$t('Components.Forms.PriceTable.Labels.ProductLabel')"
            hide-details
            prepend-inner-icon="mdi-animation"
            disabled
          />
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-autocomplete
            v-model="filterableVariantUid"
            color="teal lighten-1"
            item-color="teal lighten-1"
            :label="$t('Components.Forms.PriceTable.Labels.VariantLabel')"
            hide-details
            :items="variantFilters"
            prepend-inner-icon="mdi-view-grid-outline"
            item-text="name"
            item-value="uid"
            clearable
            :disabled="isEditing"
            @change="prices = getPrices()"
            @click:clear="filterableVariantUid = null"
          />
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-autocomplete
            v-model="filterableResellerUid"
            color="light-blue lighten-1"
            item-color="light-blue lighten-1"
            :label="$t('Components.Forms.PriceTable.Labels.ResellerLabel')"
            hide-details
            :items="resellers"
            prepend-inner-icon="mdi-shopping"
            item-text="name"
            item-value="uid"
            clearable
            :disabled="isEditing"
            @change="prices = getPrices()"
            @click:clear="filterableResellerUid = null"
          />
        </v-col>
      </v-row>

      <v-card-text v-if="!priceTiers || priceTiers.length === 0">
        <div class="display-1 text--primary text-center">
          {{ $t('Components.Forms.PriceTable.Messages.NoPricePointsDefined') }}
        </div>
      </v-card-text>

      <v-data-table
        v-if="prices && prices.length > 0 && (!isEditing || editingPricesForProduct)"
        :headers="isEditing || filterableResellerUid ? SIMPLE_HEADERS : DEFAULT_HEADERS"
        :items="isEditing ? editablePrices : prices"
        :items-per-page="numPricesToDisplay"
        hide-default-footer
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <template v-for="(d, i) in getPriceDefinedAtLevels(item)">
                <tooltip
                  :key="i"
                  :label="d.text"
                  placement="left"
                  :icon="d.icon"
                  :color="d.color"
                />
              </template>
            </td>
            <td>{{ item.tierQuantity }}</td>
            <td>
              <div v-if="!(isEditing && editingPricesForProduct)">
                <span class="mdi mdi-currency-eur" />{{ parseFloat(item.currentPrice).toFixed(2) }}
              </div>
              <template v-else>
                <v-edit-dialog
                  :return-value.sync="item.currentPrice"
                  large
                  @save="saveUpdatedPriceMsrp(item)"
                >
                  <div class="font-weight-bold">
                    <span class="mdi mdi-currency-eur" />{{ parseFloat(item.currentPrice).toFixed(2) }}
                  </div>
                  <template v-slot:input>
                    <v-text-field
                      v-model="item.currentPrice"
                      prepend-icon="mdi-currency-eur"
                      :label="$t('Components.Forms.PriceTable.Labels.EditPriceLabel')"
                      single-line
                      type="number"
                      step="0.01"
                      autofocus
                      :rules="[priceValidation]"
                    />
                  </template>
                </v-edit-dialog>
              </template>
            </td>
            <td
              v-if="!(isEditing && editingPricesForProduct)"
              :class="filterableResellerUid ? 'text-right' : ''"
            >
              <div>
                <span class="mdi mdi-currency-eur" />{{ item.currentMsrp.toFixed(2) }}
              </div>
            </td>
            <td
              v-else
              class="text-right"
            >
              <template>
                <v-edit-dialog
                  :return-value.sync="item.currentMsrp"
                  large
                  @save="saveUpdatedPriceMsrp(item)"
                >
                  <div class="font-weight-bold">
                    <span class="mdi mdi-currency-eur" />{{ parseFloat(item.currentMsrp).toFixed(2) }}
                  </div>
                  <template v-slot:input>
                    <v-text-field
                      v-model="item.currentMsrp"
                      prepend-icon="mdi-currency-eur"
                      :label="$t('Components.Forms.PriceTable.Labels.EditMsrpLabel')"
                      single-line
                      type="number"
                      step="0.01"
                      autofocus
                      :rules="[msrpValidation]"
                    />
                  </template>
                </v-edit-dialog>
              </template>
            </td>
            <td
              v-if="!isEditing && !filterableResellerUid"
              class="text-right pa-0"
            >
              <v-btn
                v-if="isDeletable(item)"
                class="ma-0 pa-0"
                text
                icon
                color="red"
                @click="showDeletePriceDialog(item)"
              >
                <v-icon color="red">
                  mdi-trash-can
                </v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>

      <v-data-table
        v-if="editablePrices && editablePrices.length > 0 && isEditing && !editingPricesForProduct"
        :headers="OVERRIDE_HEADERS"
        :items="editablePrices"
        :items-per-page="numPricesToDisplay"
        hide-default-footer
        disable-sort
        disable-filtering
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <template v-for="(d, i) in getPriceDefinedAtLevels(item)">
                <tooltip
                  :key="i"
                  :label="d.text"
                  placement="left"
                  :icon="d.icon"
                  :color="d.color"
                />
              </template>
            </td>
            <td>{{ item.tierQuantity }}</td>
            <td class="text-center">
              <div
                v-if="item.fallbackPrice"
                class="grey--text text--lighten-1"
              >
                <span class="mdi mdi-currency-eur" />{{ parseFloat(item.fallbackPrice).toFixed(2) }}
              </div>
              <div
                v-else
                class="grey--text text--lighten-1"
              >
                -
              </div>
            </td>
            <td>
              <template>
                <v-edit-dialog
                  :return-value.sync="item.currentPrice"
                  large
                  @save="saveUpdatedPriceMsrp(item)"
                >
                  <div class="font-weight-bold">
                    <span class="mdi mdi-currency-eur" />{{ parseFloat(item.currentPrice).toFixed(2) }}
                  </div>
                  <template v-slot:input>
                    <v-text-field
                      v-model="item.currentPrice"
                      prepend-icon="mdi-currency-eur"
                      :label="$t('Components.Forms.PriceTable.Labels.EditPriceLabel')"
                      single-line
                      type="number"
                      step="0.01"
                      autofocus
                      :rules="[priceValidation]"
                    />
                  </template>
                </v-edit-dialog>
              </template>
            </td>
            <td class="text-center">
              <div
                v-if="item.fallbackMsrp"
                class="grey--text text--lighten-1"
              >
                <span class="mdi mdi-currency-eur" />{{ parseFloat(item.fallbackMsrp).toFixed(2) }}
              </div>
              <div
                v-else
                class="grey--text text--lighten-1"
              >
                -
              </div>
            </td>
            <td
              class="text-right"
            >
              <template>
                <v-edit-dialog
                  :return-value.sync="item.currentMsrp"
                  large
                  @save="saveUpdatedPriceMsrp(item)"
                >
                  <div class="font-weight-bold">
                    <span class="mdi mdi-currency-eur" />{{ parseFloat(item.currentMsrp).toFixed(2) }}
                  </div>
                  <template v-slot:input>
                    <v-text-field
                      v-model="item.currentMsrp"
                      prepend-icon="mdi-currency-eur"
                      :label="$t('Components.Forms.PriceTable.Labels.EditMsrpLabel')"
                      single-line
                      type="number"
                      step="0.01"
                      autofocus
                      :rules="[msrpValidation]"
                    />
                  </template>
                </v-edit-dialog>
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>

      <template v-slot:actions>
        <v-row
          no-gutters
          align="center"
        >
          <v-col
            cols="auto"
            class="mr-auto"
          />
          <v-col cols="auto">
            <v-btn
              v-if="!isEditing"
              class="font-weight-light v-btn--tight"
              color="warning"
              @click="toggleEditPriceState"
            >
              {{ $t('Components.Forms.PriceTable.Buttons.EditPrices') }}
            </v-btn>
            <v-btn
              v-if="editingPricesForReseller && editablePrices[0].resellerUid"
              class="font-weight-light"
              color="error"
              @click="showRevokeResellerPricesDialog"
            >
              {{ $t('Components.Forms.PriceTable.Buttons.RevokeResellerPrices') }}
            </v-btn>
            <v-btn
              v-if="isEditing"
              :disabled="!hasUnsavedEdits"
              class="font-weight-light"
              color="success"
              @click="saveUpdatedPrices"
            >
              {{ $t('Components.Forms.PriceTable.Buttons.SavePrices') }}
            </v-btn>
            <v-btn
              v-if="isEditing"
              class="font-weight-light v-btn--tight"
              color="warning"
              @click="discardPriceEdit"
            >
              {{ $t('Components.Forms.PriceTable.Buttons.ExitEditMode') }}
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </base-material-card>

    <v-dialog
      v-if="newUpdatedPriceDialog"
      v-model="newUpdatedPriceDialog"
      max-width="600px"
    >
      <v-card>
        <v-card-title>
          {{ $t('Components.Forms.PriceTable.Dialogs.NewPrice.Title') }}
          <v-spacer />
          <v-icon
            :aria-label="$t('General.Buttons.Close')"
            @click="closeNewPriceDialog()"
          >
            mdi-close
          </v-icon>
        </v-card-title>

        <v-card-text>
          <v-form ref="form">
            <v-container>
              <div>
                {{ $t('Components.Forms.PriceTable.Dialogs.NewPrice.Text') }}
                <span v-if="filterableResellerUid">
                  {{ $t('Components.Forms.PriceTable.Dialogs.NewPrice.ResellerPronoun') }} <span class="light-blue--text text--lighten-1"><v-icon
                    class="ml-1"
                    color="light-blue lighten-1"
                  >mdi-shopping</v-icon> {{ getSelectedResellerName() }}</span>
                </span>
                <span v-if="!filterableVariantUid">
                  {{ $t('Components.Forms.PriceTable.Dialogs.NewPrice.ProductPronoun') }} <span class="grey--text"><v-icon
                    class="ml-1"
                    color="grey"
                  >mdi-animation</v-icon> {{ product.name }}</span>
                </span>
                <span v-else>
                  {{ $t('Components.Forms.PriceTable.Dialogs.NewPrice.VariantPronoun') }} <span class="teal--text text--lighten-1"><v-icon
                    class="ml-1"
                    color="teal lighten--1"
                  >mdi-view-grid-outline</v-icon> {{ getSelectedVariantName() }}</span>
                </span>
              </div>
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-text-field
                    v-model.number="newUpdatedPriceDialog.tierQuantity"
                    :label="$t('Components.Forms.PriceTable.Dialogs.NewPrice.FormLabels.TierQuantity')"
                    type="number"
                    :rules="[tierQuantityValidation, checkForDuplicateTiers]"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-text-field
                    v-model.number="newUpdatedPriceDialog.price"
                    :label="$t('Components.Forms.PriceTable.Dialogs.NewPrice.FormLabels.Price')"
                    class="purple-input"
                    prepend-icon="mdi-currency-eur"
                    type="number"
                    step="0.01"
                    :rules="[ priceValidation ]"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-text-field
                    v-model.number="newUpdatedPriceDialog.msrp"
                    :label="$t('Components.Forms.PriceTable.Dialogs.NewPrice.FormLabels.Msrp')"
                    class="purple-input"
                    prepend-icon="mdi-currency-eur"
                    type="number"
                    step="0.01"
                    :rules="[ msrpValidation ]"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="mr-3"
            text
            @click.stop="closeNewPriceDialog()"
          >
            {{ $t('General.Buttons.Close') }}
          </v-btn>

          <debounce-v-btn
            color="success"
            text
            @click="saveNewPrice()"
          >
            {{ $t('General.Buttons.Save') }}
          </debounce-v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="revokeResellerPricesDialog"
      v-model="revokeResellerPricesDialog"
      max-width="300px"
    >
      <v-card>
        <v-card-title>
          {{ $t('Components.Forms.PriceTable.Dialogs.RevokeResellerPrices.Title') }}
          <v-spacer />
          <v-icon
            :aria-label="$t('General.Buttons.Close')"
            @click="closeRevokeResellerPricesDialog()"
          >
            mdi-close
          </v-icon>
        </v-card-title>

        <v-card-text>
          <v-form ref="form">
            <v-container>
              <div>
                {{ $t('Components.Forms.PriceTable.Dialogs.RevokeResellerPrices.Text') }}
                <span v-if="filterableResellerUid">
                  {{ $t('Components.Forms.PriceTable.Dialogs.RevokeResellerPrices.ResellerPronoun') }} <span class="light-blue--text text--lighten-1"><v-icon
                    class="ml-1"
                    color="light-blue lighten-1"
                  >mdi-shopping</v-icon> {{ getSelectedResellerName() }}</span>
                </span>
                <span v-if="!filterableVariantUid">
                  {{ $t('Components.Forms.PriceTable.Dialogs.RevokeResellerPrices.ProductPronoun') }} <span class="grey--text"><v-icon
                    class="ml-1"
                    color="grey"
                  >mdi-animation</v-icon> {{ product.name }}</span>
                </span>
                <span v-else>
                  {{ $t('Components.Forms.PriceTable.Dialogs.RevokeResellerPrices.VariantPronoun') }} <span class="teal--text text--lighten-1"><v-icon
                    class="ml-1"
                    color="teal lighten--1"
                  >mdi-view-grid-outline</v-icon> {{ getSelectedVariantName() }}</span>
                </span>
                ?
              </div>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="mr-3"
            text
            @click.stop="closeRevokeResellerPricesDialog()"
          >
            {{ $t('Components.Forms.PriceTable.Dialogs.RevokeResellerPrices.CancelLabel') }}
          </v-btn>

          <debounce-v-btn
            color="warning"
            text
            @click="revokeResellerPrices()"
          >
            {{ $t('Components.Forms.PriceTable.Dialogs.RevokeResellerPrices.RevokePricesLabel') }}
          </debounce-v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="deletePriceDialog"
      v-model="deletePriceDialog"
      max-width="300px"
    >
      <v-card>
        <v-card-title>
          {{ $t('Components.Forms.PriceTable.Dialogs.DeletePrice.Title') }}
          <v-spacer />
          <v-icon
            :aria-label="$t('General.Buttons.Close')"
            @click="closeDeletePriceDialog()"
          >
            mdi-close
          </v-icon>
        </v-card-title>

        <v-card-text>
          <v-form ref="form">
            <v-container>
              <div>
                {{ $t('Components.Forms.PriceTable.Dialogs.DeletePrice.Text') }} <span class="font-weight-bold">{{ $t('Components.Forms.PriceTable.Dialogs.DeletePrice.TierQuantityTextBold') }} {{ deletePriceDialog.tierQuantity }}</span>
                <span v-if="!filterableVariantUid">
                  {{ $t('Components.Forms.PriceTable.Dialogs.DeletePrice.ProductPronoun') }} <span class="grey--text"><v-icon
                    class="ml-1"
                    color="grey"
                  >mdi-animation</v-icon> {{ product.name }}</span>
                </span>
                <span v-else>
                  {{ $t('Components.Forms.PriceTable.Dialogs.DeletePrice.VariantPronoun') }} <span class="teal--text text--lighten-1"><v-icon
                    class="ml-1"
                    color="teal lighten--1"
                  >mdi-view-grid-outline</v-icon> {{ getSelectedVariantName() }}</span>
                </span>
                ?
              </div>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="mr-3"
            text
            @click.stop="closeDeletePriceDialog()"
          >
            {{ $t('Components.Forms.PriceTable.Dialogs.DeletePrice.CancelLabel') }}
          </v-btn>

          <debounce-v-btn
            color="warning"
            text
            @click="deletePriceItem()"
          >
            {{ $t('Components.Forms.PriceTable.Dialogs.DeletePrice.DeleteLabel') }}
          </debounce-v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
  import adminProductService from '@/services/Admin/productService'
  import validation from '@/helpers/validationHelper'
  import tooltip from '../../components/custom/Tooltip.vue'

  export default {
    name: 'PricingTableEditable',
    components: {
      tooltip,
    },
    props: {
      productUid: {
        type: String,
        required: true,
        default: null,
      },
      productVariantUid: {
        type: String,
        required: false,
        default: null,
      },
      resellerUid: {
        type: String,
        required: false,
        default: null,
      },
      numPricesToDisplay: {
        type: Number,
        required: false,
        default: 100,
      },
    },
    data: () => ({
      priceTiers: [],
      prices: [],
      editablePrices: [],
      isEditing: false,
      hasUnsavedEdits: false,
      product: {},
      filterableResellerUid: null,
      filterableVariantUid: null,
      variantFilters: [],
      headers: [],
      newUpdatedPriceDialog: null,
      deletePriceDialog: null,
      revokeResellerPricesDialog: null,
      currentPriceLevel: {},
    }),
    computed: {
      products () {
        return this.$store.state.productsAdmin.adminList
      },
      resellers () {
        return this.$store.state.resellers.list
      },
      editingPricesForProduct () {
        return this.isEditing && !this.filterableVariantUid && !this.filterableResellerUid
      },
      editingPricesForReseller () {
        return this.filterableResellerUid && this.isEditing
      },
      // we need these 2 clean properties to ensure we never have an undefined in these fields as those values are incompatible with the api model
      cleanFilterableResellerUid () {
        return this.filterableResellerUid ? this.filterableResellerUid : null
      },
      cleanFilterableVariantUid () {
        return this.filterableVariantUid ? this.filterableVariantUid : null
      },
    },
    created: async function () {
      this.DICT_ROUTE = 'Components.Forms.PriceTable.'
      // We need to define these data properties here, because else they get wrapped in reactivity
      // easier to maintain the different headers this way than to manipulate the array
      this.DEFAULT_HEADERS = [
        { sortable: false, text: '' },
        {
          text: this.$t(this.DICT_ROUTE + 'TableHeaders.TierQuantity'),
          align: 'start',
          sortable: true,
        },
        { text: this.$t(this.DICT_ROUTE + 'TableHeaders.Price') },
        { text: this.$t(this.DICT_ROUTE + 'TableHeaders.Msrp') },
        { text: this.$t(this.DICT_ROUTE + 'TableHeaders.Actions'), align: 'end' },
      ]
      this.SIMPLE_HEADERS = [
        { sortable: false, text: '' },
        {
          text: this.$t(this.DICT_ROUTE + 'TableHeaders.TierQuantity'),
          align: 'start',
          sortable: true,
        },
        { text: this.$t(this.DICT_ROUTE + 'TableHeaders.Price') },
        { text: this.$t(this.DICT_ROUTE + 'TableHeaders.Msrp'), align: 'end' },
      ]
      this.OVERRIDE_HEADERS = [
        { text: '' },
        {
          text: this.$t(this.DICT_ROUTE + 'TableHeaders.TierQuantity'),
          align: 'start',
          sortable: true,
        },
        { text: this.$t(this.DICT_ROUTE + 'TableHeaders.FallbackPrice'), class: 'grey--text text--lighten-1' },
        { text: this.$t(this.DICT_ROUTE + 'TableHeaders.Price') },
        { text: this.$t(this.DICT_ROUTE + 'TableHeaders.FallbackMsrp'), class: 'grey--text text--lighten-1' },
        { text: this.$t(this.DICT_ROUTE + 'TableHeaders.Msrp'), align: 'end' },
      ]

      this.headers = this.DEFAULT_HEADERS
      if (this.$store.state.productsAdmin.adminList.length === 0) {
        await this.$store.dispatch('RefreshAdminProducts')
      }
      if (this.resellers.length < 1) {
        await this.$store.dispatch('RefreshResellers')
      }
      this.filterableResellerUid = this.resellerUid
      this.filterableVariantUid = this.productVariantUid
      await this.refreshPrices()

      if (!this.productUid) {
        return
      }

      var currentProd = this.products.find(
        el => el.uid === this.productUid
      )
      if (!currentProd) {
        await this.$store.dispatch('RefreshAdminProducts')
        currentProd = this.products.find(
          el => el.uid === this.productUid
        )
        if (!currentProd) {
          console.log('[Unable to find product by UID]')
          return
        }
      }

      this.product = currentProd
      this.variantFilters = currentProd.variants
    },
    methods: {
      async refreshPrices () {
        var priceTiersApiResult = await adminProductService.getAdminPrices(this.productUid)
        if (priceTiersApiResult.success === false) {
          return
        }
        this.priceTiers = priceTiersApiResult.value
        this.prices = this.getPrices()
      },
      // crud
      async saveNewPrice () {
        if (this.$refs.form.validate() !== true) {
          return false
        }
        var loadingId = await this.$store.dispatch('AddLoadingMessage', this.$t(this.DICT_ROUTE + 'Messages.SubmitCreatePriceLoadingMessage'))
        var createPriceApiResult = await adminProductService.createPrice(this.newUpdatedPriceDialog)

        if (createPriceApiResult.success === false) {
          this.$store.dispatch('RemoveLoadingMessage', loadingId)
          return false
        }
        this.priceTiers = createPriceApiResult.value
        if (this.isEditing) {
          this.editablePrices = this.getPrices()
        } else {
          this.prices = this.getPrices()
        }
        this.$store.dispatch('RemoveLoadingMessage', loadingId)
        return this.closeNewPriceDialog()
      },
      async saveUpdatedPrices () {
        var loadingId = await this.$store.dispatch('AddLoadingMessage', this.$t(this.DICT_ROUTE + 'Messages.SubmitEditLoadingMessage'))
        var updatePriceApiResult = await adminProductService.updatePrices(this.productUid, this.editablePrices)
        this.$store.dispatch('RemoveLoadingMessage', loadingId)

        if (updatePriceApiResult.success === false) {
          return
        }

        this.priceTiers = updatePriceApiResult.value
        this.prices = this.getPrices()
        this.isEditing = false
        this.hasUnsavedEdits = false
      },
      async deletePriceItem () {
        var loadingId = await this.$store.dispatch('AddLoadingMessage', this.$t(this.DICT_ROUTE + 'Messages.SubmitDeletePriceLoadingMessage'))
        var deletePriceApiResult = await adminProductService.deletePrice(this.deletePriceDialog)

        if (deletePriceApiResult.success === false) {
          this.$store.dispatch('RemoveLoadingMessage', loadingId)
          return false
        }
        this.priceTiers = deletePriceApiResult.value
        this.prices = this.getPrices()
        this.deletePriceDialog = null
        this.$store.dispatch('RemoveLoadingMessage', loadingId)
        return true
      },
      async revokeResellerPrices () {
        var loadingId = await this.$store.dispatch('AddLoadingMessage', this.$t(this.DICT_ROUTE + 'Messages.SubmitDeleteResellerPricesLoadingMessage'))
        var revokeResellerPricesApiResult = await adminProductService.revokeResellerPrices(this.revokeResellerPricesDialog)

        if (revokeResellerPricesApiResult.success === false) {
          this.$store.dispatch('RemoveLoadingMessage', loadingId)
          return false
        }
        this.priceTiers = revokeResellerPricesApiResult.value
        this.editablePrices = this.getPrices()
        this.revokeResellerPricesDialog = null
        this.$store.dispatch('RemoveLoadingMessage', loadingId)
        return true
      },
      // price manipulation
      isDeletable (priceItem) {
        return (this.cleanFilterableVariantUid && priceItem.productVariantUid) || (!this.cleanFilterableResellerUid && priceItem.resellerUid == null && !this.cleanFilterableVariantUid && priceItem.productVariantUid == null)
      },
      getPrices () {
        var filteredPrices = []
        var pricesTiers = JSON.parse(JSON.stringify(this.priceTiers)) // Need to do this to clean any references from the original price tiers
        for (var i = 0; i < pricesTiers.length; i++) {
          // specific tier
          var superSpecific = pricesTiers[i].find((price) => {
            return price.resellerUid === this.cleanFilterableResellerUid && price.productVariantUid === this.cleanFilterableVariantUid
          })

          if (!superSpecific) {
            var semiSpecific = pricesTiers[i].find((price) => {
              return (this.cleanFilterableVariantUid && this.cleanFilterableResellerUid &&
                price.productVariantUid === this.cleanFilterableVariantUid && price.resellerUid === null)
            })

            if (!semiSpecific) {
              var generic = pricesTiers[i].find((price) => {
                return price.resellerUid === null && price.productVariantUid === null
              })
              // using price for product
              if (generic) {
                this.setFallbackValues(generic, pricesTiers)
                filteredPrices.push(generic)
              }
            } else {
              // using price matching current selected variant
              this.setFallbackValues(semiSpecific, pricesTiers)
              filteredPrices.push(semiSpecific)
            }
          } else {
            // using price matching current reseller and variant selection
            this.setFallbackValues(superSpecific, pricesTiers)
            filteredPrices.push(superSpecific)
          }
        }
        return filteredPrices
      },
      setFallbackValues (priceItem, priceTiers) {
        if (!priceTiers) {
          priceTiers = JSON.parse(JSON.stringify(this.priceTiers))
        }

        var priceTier = priceTiers.find(val => {
          return val.length > 0 && val[0].tierQuantity === priceItem.tierQuantity
        })

        var fallbackPriceItem = priceTier.find((price) => {
          return price.resellerUid === null && price.productVariantUid === null
        })

        if (this.cleanFilterableResellerUid) {
          // if we have a reseller uid selected, check to see if it has a variant level fallback
          var variantDefinedPrice = priceTier.find((price) => {
            return price.productVariantUid === this.cleanFilterableVariantUid && price.resellerUid === null
          })
          if (variantDefinedPrice) {
            fallbackPriceItem = variantDefinedPrice
          }
        }

        if (fallbackPriceItem) {
          priceItem.fallbackPrice = fallbackPriceItem.currentPrice
          priceItem.fallbackMsrp = fallbackPriceItem.currentMsrp
        }

        // if we have a selected reseller, then all posted editable prices will have the same reseller/variant context
        if (this.cleanFilterableResellerUid) {
          priceItem.newProductVariantUid = this.cleanFilterableVariantUid
          priceItem.newResellerUid = this.cleanFilterableResellerUid
        }
      },
      saveUpdatedPriceMsrp (priceItem) {
        priceItem.newProductVariantUid = this.cleanFilterableVariantUid
        this.hasUnsavedEdits = true
      },
      // pure UI
      toggleEditPriceState () {
        this.editablePrices = this.getPrices()
        this.isEditing = true
      },
      discardPriceEdit () {
        this.isEditing = false
        this.hasUnsavedEdits = false
        this.prices = this.getPrices()
      },
      showNewPricePointDialog () {
        this.newUpdatedPriceDialog = {
          productUid: this.productUid,
          productVariantUid: this.cleanFilterableVariantUid,
          resellerUid: this.cleanFilterableResellerUid,
        }
      },
      closeNewPriceDialog () {
        this.newUpdatedPriceDialog = null
      },
      showDeletePriceDialog (item) {
        this.deletePriceDialog = {
          productUid: this.productUid,
          priceUid: item.uid,
          tierQuantity: item.tierQuantity,
        }
      },
      closeDeletePriceDialog () {
        this.deletePriceDialog = null
      },
      showRevokeResellerPricesDialog () {
        this.revokeResellerPricesDialog = {
          productUid: this.productUid,
          resellerUid: this.cleanFilterableResellerUid,
          productVariantUid: this.cleanFilterableVariantUid,
        }
      },
      closeRevokeResellerPricesDialog () {
        this.revokeResellerPricesDialog = null
      },
      getPriceDefinedAtLevels (item) {
        var defaultIcon = { icon: 'animation', text: this.$t(this.DICT_ROUTE + 'Tooltips.DefinedAtProductLevel'), color: 'grey lighten-1' }

        if (item) {
          var icons = []
          if (item.resellerUid) {
            icons.push({ icon: 'shopping', text: this.$t(this.DICT_ROUTE + 'Tooltips.DefinedAtResellerLevel'), color: 'light-blue lighten-1' })
          }
          if (item.productVariantUid) {
            icons.push({ icon: 'view-grid-outline', text: this.$t(this.DICT_ROUTE + 'Tooltips.DefinedAtVariantLevel'), color: 'teal lighten-1' })
          }
          if (!item.resellerUid && !item.productVariantUid) {
            icons.push(defaultIcon)
          }
          return icons
        }

        return [defaultIcon]
      },
      getSelectedResellerName () {
        var selectedReseller = this.resellers.find(r => {
          return r.uid === this.cleanFilterableResellerUid
        })
        return selectedReseller ? selectedReseller.name : this.$t(this.DICT_ROUTE + 'Messages.NoReseller')
      },
      getSelectedVariantName () {
        var selectedVariant = this.product.variants.find(v => {
          return v.uid === this.cleanFilterableVariantUid
        })
        return selectedVariant ? selectedVariant.name : this.$t(this.DICT_ROUTE + 'Messages.NoVariant')
      },
      // validation
      checkForDuplicateTiers (value) {
        var currentTiers = this.getPrices()
        var matchingTier = currentTiers.find(tier => {
          return tier.tierQuantity === parseInt(value)
        })
        return !matchingTier || this.$t(this.DICT_ROUTE + 'Validation.TierQuantityAlreadyDefined')
      },
      tierQuantityValidation (value) {
        return (validation.numberIsNotNull(value) && validation.numberIsGreaterThanZero(value)) || this.$t(this.DICT_ROUTE + 'Validation.TierQuantityRequired')
      },
      priceValidation (value) {
        return (validation.numberIsNotNull(value) && validation.numberIsGreaterThanZero(value)) || this.$t(this.DICT_ROUTE + 'Validation.PriceRequired')
      },
      msrpValidation (value) {
        return (validation.numberIsNotNull(value) && validation.numberIsGreaterThanZero(value)) || this.$t(this.DICT_ROUTE + 'Validation.MsrpRequired')
      },
    },
  }
</script>
